<!--
  Job Hazard Analysis.

  Header (work description, location, scheduled start date, attendees), the
  three-column step table, then prepared-by/reviewed-by. Field order mirrors
  the printed sheet. Follows MixDesignForm.svelte's shape throughout: raw
  string state, an explicit Save, add/remove/reorder step rows, delete behind
  an in-page confirmation banner rather than confirm(). Every field here is
  already a plain string on the wire (no numeric-looking cells), so there is
  no toNumber/toInput round trip to worry about.

  A brand-new JHA starts with no steps on the server — the paper's blank rows
  are space, not content — but an entirely empty table gives the user nothing
  to type into, so the client offers one blank row itself.

  No acceptance verdicts: this form records what was planned, not a judgement.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { createAutosave } from '../../lib/autosave';
  import { printJha } from '../../lib/export/quality-pdf';
  import SitePicker from './SitePicker.svelte';
  // Aliased: this component has its own `location` field (the street
  // address on the sheet), which would otherwise shadow the router store.
  import { claimAddress, linkHandler, location as appLocation, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { advanceOnEnter } from '../../lib/fields/advance-on-enter';
  import { isAdmin, notify, notifyError } from '../../lib/store';
  import type { JhaStepInput, SaveJhaRequest, SiteSummary } from '../../lib/types';

  interface Props {
    /** null for a new JHA. */
    jhaId?: number | null;
  }

  let { jhaId = null }: Props = $props();

  /** A step row as held by the form. Wire shape (JhaStepInput) is identical. */
  type StepRow = JhaStepInput;

  let loading = $state(jhaId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new JHA gets one on its first save. */
  let savedId = $state<number | null>(jhaId);

  let siteId = $state<number | null>(null);
  let jobsite = $state('');
  let workDescription = $state('');
  let location = $state('');
  let scheduledStartDate = $state('');
  let attendees = $state('');
  let preparedBy = $state('');
  let preparedDate = $state('');
  let reviewedBy = $state('');
  let reviewedDate = $state('');

  function blankStep(): StepRow {
    return { sequence_of_work: '', potential_hazards: '', control_method: '' };
  }

  let steps = $state<StepRow[]>(jhaId === null ? [blankStep()] : []);

  // Opened from a site page ("New JHA for Kingsway"): pre-select that site
  // and fill the jobsite text from it. Only ever on a NEW form, and only
  // into a field still empty, so this can never overwrite something the
  // user has typed or loaded.
  $effect(() => {
    const fromSite = siteIdFromQuery($appLocation.query);
    if (jhaId === null && fromSite !== null && siteId === null) {
      siteId = fromSite;
      api
        .getSite(fromSite)
        .then((response) => {
          if (!jobsite.trim()) jobsite = response.site.name;
        })
        .catch(() => {
          // The typed jobsite text still saves even if the name lookup fails.
        });
    }
  });

  function chooseSite(site: SiteSummary | null) {
    siteId = site?.id ?? null;
    if (site) jobsite = site.name;
  }

  $effect(() => {
    if (jhaId === null) return;
    loading = true;
    api
      .getJha(jhaId)
      .then((response) => {
        const jha = response.jha;
        siteId = jha.site_id;
        jobsite = jha.jobsite;
        workDescription = jha.work_description;
        location = jha.location;
        scheduledStartDate = jha.scheduled_start_date;
        attendees = jha.attendees;
        preparedBy = jha.prepared_by;
        preparedDate = jha.prepared_date;
        reviewedBy = jha.reviewed_by;
        reviewedDate = jha.reviewed_date;
        steps = response.steps.map((step) => ({
          sequence_of_work: step.sequence_of_work,
          potential_hazards: step.potential_hazards,
          control_method: step.control_method
        }));
        savedId = jha.id;
        // The loaded document is the auto-save baseline: nothing is dirty yet.
        lastSaved = JSON.stringify(body());
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that JHA');
        error = 'Could not load that JHA.';
      })
      .finally(() => {
        loading = false;
      });
  });

  function addStep() {
    steps = [...steps, blankStep()];
  }

  function removeStep(index: number) {
    steps = steps.filter((_, i) => i !== index);
  }

  function moveStep(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    steps = next;
  }

  function body(): SaveJhaRequest {
    return {
      site_id: siteId,
      jobsite,
      work_description: workDescription,
      location,
      scheduled_start_date: scheduledStartDate,
      attendees,
      prepared_by: preparedBy,
      prepared_date: preparedDate,
      reviewed_by: reviewedBy,
      reviewed_date: reviewedDate,
      steps: steps.map((step) => ({ ...step }))
    };
  }

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    const started = printJha(
      {
        jha: {
          id: savedId ?? 0,
          jobsite,
          site_id: siteId,
          work_description: workDescription,
          location,
          scheduled_start_date: scheduledStartDate,
          attendees,
          prepared_by: preparedBy,
          prepared_date: preparedDate,
          reviewed_by: reviewedBy,
          reviewed_date: reviewedDate,
          created_at: stamp,
          updated_at: stamp
        },
        steps: steps.map((step, index) => ({
          id: index + 1,
          jha_id: savedId ?? 0,
          seq: index + 1,
          sequence_of_work: step.sequence_of_work,
          potential_hazards: step.potential_hazards,
          control_method: step.control_method
        })),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save(opts: { auto?: boolean } = {}) {
    if (saving || removed) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const response =
        savedId === null ? await api.createJha(body()) : await api.updateJha(savedId, body());

      const isNew = savedId === null;
      savedId = response.jha.id;
      // The baseline is what the form NOW holds, so nothing reads as a fresh edit.
      lastSaved = JSON.stringify(body());
      if (!opts.auto) notify({ tone: 'ok', title: isNew ? 'JHA created' : 'JHA saved' });
      // A new JHA gets its own address, so a refresh or a back button lands
      // on the saved sheet rather than a blank one. An AUTO save only
      // rewrites the address bar: `navigate` would change the route param and
      // the {#key} around this screen would tear the form down mid-keystroke.
      if (isNew) {
        if (opts.auto) claimAddress(routePath.qualityJha(savedId));
        else navigate(routePath.qualityJha(savedId), { replace: true, keepScroll: true });
      }
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /* --- auto-save (lib/autosave.ts; same wiring as DailyReportForm) -------- */

  /** JSON of the document at its last save/load — the dirty check's anchor. */
  let lastSaved = $state<string | null>(null);
  /** Set once the JHA is deleted, so no timer can quietly resurrect it. */
  let removed = false;

  /**
   * Does a NEW form hold something worth creating a record for? The site
   * prefill (siteId + jobsite) deliberately does not count — only something
   * a person typed.
   */
  function hasContent(): boolean {
    return (
      workDescription.trim() !== '' ||
      location.trim() !== '' ||
      scheduledStartDate.trim() !== '' ||
      attendees.trim() !== '' ||
      preparedBy.trim() !== '' ||
      preparedDate.trim() !== '' ||
      reviewedBy.trim() !== '' ||
      reviewedDate.trim() !== '' ||
      steps.some(
        (s) => s.sequence_of_work.trim() !== '' || s.potential_hazards.trim() !== '' || s.control_method.trim() !== ''
      )
    );
  }

  const dirty = $derived(lastSaved !== null && JSON.stringify(body()) !== lastSaved);

  const autosave = createAutosave({
    snapshot: () => JSON.stringify(body()),
    saved: () => lastSaved,
    busy: () => saving || deleting,
    blocked: () => removed,
    ready: () => savedId !== null || hasContent(),
    save: () => void save({ auto: true })
  });

  $effect(() => {
    const snapshot = JSON.stringify(body()); // subscribes to every field
    if (loading) return;
    if (snapshot === lastSaved) return;
    autosave.changed();
  });

  // A timer must not outlive the form (navigation away mid-edit).
  $effect(() => {
    return () => autosave.stop();
  });

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteJha(savedId);
      // No auto-save timer may quietly resurrect what was just deleted.
      removed = true;
      autosave.stop();
      notify({ tone: 'ok', title: 'JHA deleted' });
      navigate(routePath.qualityJhaList(siteId ?? undefined), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that JHA');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

<!-- Enter moves to the next field; see lib/fields/advance-on-enter.ts. -->
<div use:advanceOnEnter>
{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <div class="titlebar">
        <h1 class="card__title">{savedId === null ? 'New JHA' : workDescription || 'Job Hazard Analysis'}</h1>
        <a class="btn btn--sm" href={routePath.qualityJhaList(siteId ?? undefined)} onclick={linkHandler}>All JHAs</a>
      </div>

      <SitePicker value={siteId} onchoose={chooseSite} />

      <!--
        Jobsite prints on the sheet. Choosing a site above fills it in; it
        stays free text so a record from before sites, or a site not on the
        list yet, is never blocked or overwritten against the user's wishes.
      -->
      <label class="field">
        <span class="label">Jobsite</span>
        <input
          type="text"
          bind:value={jobsite}
          placeholder="Kits, Kingsway, Demo…"
        />
        <span class="field__hint">Filled in from the site; edit only if the sheet should say something different.</span>
      </label>

      <label class="field">
        <span class="label">Work description</span>
        <textarea rows="2" bind:value={workDescription} placeholder="Description of work"></textarea>
        {#if fieldErrors.work_description}<span class="field__flag">{fieldErrors.work_description}</span>{/if}
      </label>

      <label class="field">
        <span class="label">Location</span>
        <input type="text" bind:value={location} placeholder="Street address" />
      </label>

      <label class="field">
        <span class="label">Scheduled start date</span>
        <input type="date" bind:value={scheduledStartDate} />
      </label>

      <label class="field">
        <span class="label">Attendees</span>
        <textarea rows="2" bind:value={attendees} placeholder="Names of those present"></textarea>
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Steps</h2>

      <!--
        Same reasoning and same mechanism as MixDesignForm's materials grid:
        below 60rem the three step fields stack, one to a block, each with
        its own visible label; at and above 60rem the row becomes one line
        under a single set of headings via the shared --stepcols template,
        and .step__fields switches to display:contents so its children join
        the row's own grid columns. Unlike the materials grid there is no
        auto-fit sub-grid on narrow widths: three full-width text fields read
        better stacked than squeezed into a small-field grid.
      -->
      <div class="steps">
        <div class="steps__head" aria-hidden="true">
          <span>Sequence of Work</span>
          <span>Potential Hazards</span>
          <span>Method for Eliminating or Controlling and Person Responsible</span>
          <span></span>
        </div>

        {#each steps as step, index (index)}
          <div class="step">
            <!--
              Identity and controls at the TOP of the block, together. Stacked on
              a phone a step is taller than the screen, and with the controls at
              the bottom the nearest visible X belonged to the step ABOVE the
              fields you were reading — so people removed the wrong one. Same
              defect that was reported on the field test log.
            -->
            <div class="step__head">
              <span class="step__name">Step {index + 1}</span>
              <div class="step__actions">
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => moveStep(index, -1)}
                  disabled={index === 0}
                  aria-label="Move step {index + 1} up"
                >↑</button>
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => moveStep(index, 1)}
                  disabled={index === steps.length - 1}
                  aria-label="Move step {index + 1} down"
                >↓</button>
                <button
                  class="btn btn--sm btn--ghost"
                  type="button"
                  onclick={() => removeStep(index)}
                  aria-label="Remove step {index + 1}"
                >✕</button>
              </div>
            </div>

            <div class="step__fields">
              <label class="step__field">
                <span class="label">Sequence of Work</span>
                <textarea
                  rows="2"
                  bind:value={step.sequence_of_work}
                  aria-label="Step {index + 1} sequence of work"
                ></textarea>
              </label>

              <label class="step__field">
                <span class="label">Potential Hazards</span>
                <textarea
                  rows="2"
                  bind:value={step.potential_hazards}
                  aria-label="Step {index + 1} potential hazards"
                ></textarea>
              </label>

              <label class="step__field">
                <span class="label">
                  Method for Eliminating or Controlling and Person Responsible
                </span>
                <textarea
                  rows="2"
                  bind:value={step.control_method}
                  aria-label="Step {index + 1} method for eliminating or controlling and person responsible"
                ></textarea>
              </label>
            </div>

          </div>
        {/each}
      </div>

      <button class="btn btn--block" type="button" onclick={addStep}>Add step</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <div class="grid2">
        <label class="field">
          <span class="label">Prepared by</span>
          <input type="text" bind:value={preparedBy} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={preparedDate} />
        </label>
      </div>

      <div class="grid2">
        <label class="field">
          <span class="label">Reviewed by</span>
          <input type="text" bind:value={reviewedBy} />
        </label>
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={reviewedDate} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={() => save()}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save JHA{/if}
      </button>

      <!-- The auto-save's word on where things stand. Only ever informational:
           the button above still saves on demand. -->
      <p class="autosave" role="status">
        {#if saving}
          Saving…
        {:else if savedId !== null && !dirty}
          All changes saved.
        {:else if savedId !== null || hasContent()}
          Unsaved changes — saving automatically…
        {:else}
          Saves automatically as you fill it in.
        {/if}
      </p>

      {#if $isAdmin && savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this JHA?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete JHA
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}
</div>

<style>
  /* Informational, never alarming: where the auto-save stands. */
  .autosave {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
    text-align: center;
  }

  .titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .titlebar .card__title {
    margin: 0;
  }

  /* One shared column template for the header row and each one-line step
     row at 60rem+, exactly the reasoning documented on MixDesignForm's
     --matcols: sharing the string (not just the column count) is what keeps
     the header and the rows from drifting out of alignment with each other. */
  .steps {
    --stepcols: 1fr 1fr 1.3fr calc(3 * var(--tap) + 0.6rem);
  }

  .steps__head {
    display: none;
  }

  /* A card, so there is a visible edge between one step and the next — stacked
     on a phone a step is taller than the screen. */
  .step {
    border: 1px solid var(--line-soft);
    border-radius: var(--radius);
    padding: 0.6rem 0.7rem 0.85rem;
    margin-bottom: 0.7rem;
  }

  .step:last-of-type {
    margin-bottom: 0;
  }

  .step__fields {
    display: grid;
    gap: 0.6rem;
  }

  .step__field {
    display: block;
    min-width: 0;
  }

  .step__field textarea {
    min-height: 3.6rem;
  }

  .step__actions {
    display: flex;
    gap: 0.3rem;
    flex: none;
  }

  /* Sticky so the control you can see always belongs to the step you are
     reading — see the comment in the markup for why that matters here. */
  /* NOT sticky, deliberately. Pinning this header while you scroll a tall
     step would be nicer still, but an ancestor .card sets overflow:hidden,
     which disables position:sticky for everything inside it — and .card is
     shared with the field QC screens that are in production, so widening its
     overflow to buy a nicety here is not a trade worth making. The header
     naming its own step is what removes the ambiguity; being pinned would
     only have made it prettier. */
  .step__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: -0.6rem -0.7rem 0.6rem;
    padding: 0.4rem 0.7rem;
    background: var(--bg-sunken);
    border-radius: var(--radius) var(--radius) 0 0;
    border-bottom: 1px solid var(--line-soft);
  }

  .step__name {
    font-weight: 700;
    font-size: 0.9rem;
  }

  /* One line per step once there is room for it — same 60rem threshold and
     same margin-of-safety reasoning as MixDesignForm's materials grid: at
     the 64rem office container ceiling the third (widest) column alone gets
     roughly 330px, comfortably above what its textarea needs on one line. */
  @media (min-width: 60rem) {
    .steps__head {
      display: grid;
      grid-template-columns: var(--stepcols);
      column-gap: 0.6rem;
      padding: 0 0 0.4rem;
      border-bottom: 2px solid var(--line);
    }

    .steps__head span {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--ink-soft);
    }

    /* Back to one line per step, with the fields under the headings above.
       The card treatment and the "Step N" header exist for the stacked phone
       layout, where a step is taller than the screen; at this width the whole
       step is one row and the column headings already say what each field is. */
    .step {
      display: grid;
      grid-template-columns: var(--stepcols);
      column-gap: 0.6rem;
      align-items: start;
      padding: 0.5rem 0;
      border: 0;
      border-radius: 0;
      margin-bottom: 0;
      border-bottom: 1px solid var(--line-soft);
    }

    .step:last-of-type {
      border-bottom: 0;
    }

    /* The header wrapper drops out of layout too, so the only thing it
       contributes here is the action buttons — placed explicitly in the last
       column, because in source order they come BEFORE the fields and would
       otherwise auto-place into column 1 and shove every field one across.
       That is exactly what went wrong when the header was introduced. */
    .step__head {
      display: contents;
    }

    /* "Step 1" is a phone affordance. Here the row itself is the step. */
    .step__name {
      display: none;
    }

    /* The wrapper drops out of layout so its three children take their
       place directly as columns 1-3 of the row's own grid, in the document
       order they already had. */
    .step__fields {
      display: contents;
    }

    /* The heading row above now names each column; showing a field's own
       caption too would say "Potential Hazards" twice. The aria-label
       already on every textarea is what keeps the field identifiable once
       its heading is a whole row away rather than sitting right above it. */
    .step__field .label {
      display: none;
    }

    .step__actions {
      grid-column: 4;
      justify-content: flex-start;
      margin-top: 0;
      padding-top: 0.2rem;
    }
  }
</style>
